import { describeApiError } from "lib/error/problemDetails";
import { MemberLoad, resolveMemberState } from "lib/member";
import { useEffect, useMemo, useState } from "react";
import { useAPIClient } from "render/context/APIContext";
import { useAppInsights } from "render/context/AppInsightsContext";
import { useRegion } from "render/context/RegionContext";

/**
 * Resolves the member on the current route and decides whether their record may be shown
 * here. `resolveMemberState` holds that rule; this hook supplies its two inputs and reports the
 * outcome. See docs/architecture/2026-09-03-Withholding-Out-Of-Region-Members.md.
 */
export function useMember(patientId: string) {
  const api = useAPIClient().patient;
  const appInsights = useAppInsights();
  const region = useRegion();

  const [load, setLoad] = useState<MemberLoad>({ status: "loading" });

  useEffect(() => {
    let current = true;
    setLoad({ status: "loading" });

    // Started without waiting on the region, so the two requests overlap
    api
      .fetchPatient({ patientId })
      .result.then((details) => {
        if (current) {
          setLoad({ status: "loaded", details });
        }
      })
      .catch(async (error) => {
        console.error("Could not load patient:", error);
        const message = await describeApiError("Could not load patient", error);
        if (current) {
          setLoad({ status: "failed", message });
        }
      });

    return () => {
      current = false;
    };
  }, [api, patientId]);

  const state = useMemo(
    () => resolveMemberState(load, region, patientId),
    [load, region, patientId]
  );

  // Every decided check is counted, not only the ones that withhold: a flat zero on the
  // withheld outcome otherwise reads the same whether the guardrail is working or off.
  useEffect(() => {
    if (state.status === "loading") {
      return;
    }

    const outcome =
      state.status === "failed"
        ? "memberUnavailable"
        : state.status === "withheld"
        ? "withheld"
        : region.status === "resolved"
        ? "sameRegion"
        : "servingRegionUnavailable";

    appInsights.trackEvent({ name: "MemberRegionChecked" }, { outcome });

    if (state.status !== "withheld") {
      return;
    }

    // The external id only: an internal id is a database key and must not reach App Insights.
    appInsights.trackTrace({
      message: `Withheld member ${state.externalId}: data is held in ${state.memberRegion}, this app serves ${state.servingRegion}`,
      properties: {
        servingRegion: state.servingRegion,
        memberRegion: state.memberRegion,
      },
    });
  }, [state, region.status, appInsights]);

  return state;
}
